import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Task } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Table from '@lib/components/Table/Table';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import { FormTemplateTasksQueryHookResult } from 'utils/fetch/hooks/useFormTemplateTasksQueryHook';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import getActionsForSelected from './utils/getFormTemplateTasksTableActionsForSelected';
import getFormTemplateTasksTableColumns from './utils/getFormTemplateTasksTableColumns';
import FormTemplateBuilderMainBlockView from './FormTemplateBuilderMainBlockView';
import getTemplateActions from './utils/getTemplateActions';

interface Props {
  formTemplateId: string;
  formTemplateData: Values;
  formTemplateLoading: boolean;
  formTemplateVersionData: Values;
  loadingState: boolean;
  onCreateTaskHandler: () => void;
  onDuplicateHandler: () => void;
  onEditHandler: () => void;
  onFormTemplateArchiveHandler: () => void;
  openDestroyModal: () => void;
  openScheduleModal: () => void;
  onTaskFormTemplateVersionChange: (
    taskId: string,
    newFormTemplateVersionId: string,
  ) => void;
  onTaskFormTemplateVersionBulkChange: (
    newFormTemplateVersionId: string,
  ) => void;
  tasksQueryHook: FormTemplateTasksQueryHookResult;
  useBulkSelectResult: UseBulkSelectResult<Task>;
  isViewOnly: boolean;
}

export default function FormTemplateBuilderTasksView(props: Props) {
  const {
    formTemplateData,
    formTemplateId,
    formTemplateLoading,
    formTemplateVersionData,
    loadingState,
    onCreateTaskHandler,
    onDuplicateHandler,
    onEditHandler,
    onFormTemplateArchiveHandler,
    onTaskFormTemplateVersionBulkChange,
    onTaskFormTemplateVersionChange,
    openDestroyModal,
    openScheduleModal,
    tasksQueryHook,
    useBulkSelectResult,
    isViewOnly,
  } = props;
  const { tasks, tasksLoading, totalTasksCount } = tasksQueryHook;
  const { getSelectedItemsCount } = useBulkSelectResult;

  const { t } = useTranslation();

  const selectedItemsCount = getSelectedItemsCount(totalTasksCount);

  const actions = getTemplateActions({
    onDuplicateHandler,
    onEditHandler: _get(formTemplateData, 'canUpdate', false)
      ? onEditHandler
      : undefined,
    openDestroyModal: _get(formTemplateData, 'canDestroy', false)
      ? openDestroyModal
      : undefined,
    onArchiveHandler: _get(formTemplateData, 'canArchive', false)
      ? onFormTemplateArchiveHandler
      : undefined,
    t,
  });

  const tableColumns = getFormTemplateTasksTableColumns({
    formTemplateId,
    formTemplateData,
    onTaskFormTemplateVersionChange,
    t,
  });

  const tableActionsForSelected = isViewOnly
    ? []
    : getActionsForSelected({
        formTemplateId,
        loadingState,
        onVersionChange: (newValue) => {
          const newVersionId = _get(newValue, 'value');
          if (newVersionId) {
            onTaskFormTemplateVersionBulkChange(newVersionId);
          }
        },
        openScheduleModal,
        t,
        selectedItemsCount,
      });

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-tasks-yet')}
      text={<Trans i18nKey="no-tasks-yet-template" />}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="create"
                buttonText={t('create-a-task')}
                buttonSize={ButtonSizes.large}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="task_alt"
                onClick={onCreateTaskHandler}
                disabled={loadingState}
              />,
            ]
      }
    />
  );

  return (
    <DashboardContentMaxWidth>
      {formTemplateLoading ? (
        <CircleLoader />
      ) : (
        <>
          <FormTemplateBuilderMainBlockView
            actions={actions}
            formTemplateData={formTemplateData}
            formTemplateId={formTemplateId}
            formTemplateVersionData={formTemplateVersionData}
            loadingState={loadingState}
            onCreateTaskHandler={onCreateTaskHandler}
            onDuplicateHandler={onDuplicateHandler}
            isViewOnly={isViewOnly}
          />
          <Table
            actionsForSelected={tableActionsForSelected}
            columns={tableColumns}
            data={tasks}
            emptyState={emptyState}
            id="tasks"
            loading={loadingState || tasksLoading}
            totalEntries={totalTasksCount}
            useBulkSelectResult={useBulkSelectResult}
          />
        </>
      )}
    </DashboardContentMaxWidth>
  );
}
